import { Link } from 'react-router';
import { EmptyState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { WelcomeBanner } from '../../components/WelcomeBanner.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { formatNumber, formatXaf, timeAgo } from '../../lib/format.js';

const LandlordDashboard = () => {
  useTitle('Landlord dashboard');
  const { user, counts, managingFor } = useAuth();

  const properties = useFetch('/properties/mine');
  const applications = useFetch('/applications?status=any&pageSize=8');
  const rent = useFetch('/landlord/rent-roll');
  const leases = useFetch('/tenancies?status=open');

  const listings = properties.data?.items || [];
  const inbox = applications.data?.items || [];

  const live = listings.filter((item) => item.moderationStatus === 'approved' && item.status === 'available');
  const awaitingReview = listings.filter((item) => item.moderationStatus === 'pending');
  const rejected = listings.filter((item) => item.moderationStatus === 'rejected');
  const newApplications = inbox.filter((item) => item.status === 'submitted');
  const monthlyIncome = listings
    .filter((item) => item.status === 'rented')
    .reduce((sum, item) => sum + item.price, 0);
  const totalViews = listings.reduce((sum, item) => sum + (item.viewCount || 0), 0);
  const rentSummary = rent.data?.summary;
  const totalOutstanding = rentSummary?.outstanding ?? rentSummary?.activeOutstanding ?? 0;
  const totalOverdue = rentSummary?.overdue ?? rentSummary?.activeOverdue ?? 0;
  const totalTenantsBehind = rentSummary?.tenantsBehind ?? rentSummary?.activeTenantsBehind ?? 0;
  // The whole lease year is billed up front, so the balance includes months not
  // yet due. Lead with what tenants should have paid by today.
  const dueNow = rentSummary?.dueNow ?? totalOutstanding;
  const billedAhead = Math.max(totalOutstanding - dueNow, 0);
  const rentYear = rentSummary?.rentYear;
  const internetYear = rentSummary?.internetYear;
  const awaitingSignature = (leases.data?.items || []).filter(
    (lease) => lease.status === 'awaiting_signatures'
  );
  const needsMySignature = awaitingSignature.filter((lease) => !lease.signatures?.landlord);

  return (
    <div className="container section">
      <WelcomeBanner
        eyebrow="Welcome back,"
        title={user.businessName || user.name}
        text="Your listings, your applicants and how much interest each house is getting."
      >
        <Link to="/landlord/properties/new" className="btn btn-primary">
          Add a listing
        </Link>
        <Link to="/landlord/leases/new" className="btn btn-outline">
          Draw up a lease
        </Link>
      </WelcomeBanner>

      {managingFor ? (
        <div className="notice notice-info">
          Managing on behalf of <strong>{managingFor.name}</strong>.
        </div>
      ) : null}

      {user.kycStatus === 'unsubmitted' ? (
        <div className="notice notice-warn">
          <strong>Get verified.</strong> Upload an ID or business registration under{' '}
          <Link to="/profile">Profile and settings</Link> and a verified badge appears on all your listings. Tenants
          apply more readily when they can see who they are dealing with.
        </div>
      ) : null}

      {user.kycStatus === 'rejected' ? (
        <div className="notice notice-bad">
          <strong>Verification failed.</strong> The document we received was not clear enough.{' '}
          <Link to="/profile">Upload another copy</Link>.
        </div>
      ) : null}

      {rentSummary ? (
        <>
          <div className="stat-row">
            <div className="stat-tile">
              <span className="stat-value">{formatXaf(rentSummary.collectedThisMonth)}</span>
              <span className="stat-label">Payments collected this month</span>
              <span className="stat-note">
                {formatXaf(rentSummary.expectedMonthly)} expected from {rentSummary.activeLeases} lease
                {rentSummary.activeLeases === 1 ? '' : 's'}
              </span>
            </div>
            <div className={`stat-tile ${totalOverdue > 0 ? 'tone-warn' : ''}`}>
              <span className="stat-value">{formatXaf(dueNow)}</span>
              <span className="stat-label">Due now</span>
              <span className="stat-note">
                {totalOverdue > 0
                  ? `${formatXaf(totalOverdue)} overdue from ${totalTenantsBehind} tenant${totalTenantsBehind === 1 ? '' : 's'}`
                  : 'Nothing overdue'}
                {billedAhead > 0 ? ` - ${formatXaf(billedAhead)} more billed for the rest of the lease year` : ''}
              </span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">{rentSummary.activeLeases}</span>
              <span className="stat-label">Tenancies in force</span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">{awaitingSignature.length}</span>
              <span className="stat-label">Leases waiting to be signed</span>
            </div>
          </div>

          {rentYear && rentYear.expected > 0 ? (
            <div className="stat-row">
              <div className="stat-tile">
                <span className="stat-value">{formatXaf(rentYear.expected)}</span>
                <span className="stat-label">Annual rent to collect</span>
                <span className="stat-note">Across active leases, for their current lease year</span>
              </div>
              <div className="stat-tile">
                <span className="stat-value">{formatXaf(rentYear.collected)}</span>
                <span className="stat-label">Rent collected so far</span>
                <span className="stat-note">{formatXaf(rentYear.outstanding)} of the year still to collect</span>
              </div>
              {internetYear && internetYear.expected > 0 ? (
                <div className="stat-tile">
                  <span className="stat-value">{formatXaf(internetYear.expected)}</span>
                  <span className="stat-label">Annual internet to collect</span>
                  <span className="stat-note">
                    {formatXaf(internetYear.collected)} collected - {formatXaf(internetYear.outstanding)} still to
                    collect
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="row-actions dashboard-links">
            <Link to="/landlord/rent" className="link-strong">
              Open the rent roll
            </Link>
            <Link to="/landlord/leases" className="link-strong">
              All leases
            </Link>
            <Link to="/landlord/tenants" className="link-strong">
              My tenants
            </Link>
          </div>
        </>
      ) : null}

      {awaitingSignature.length > 0 ? (
        <div className="notice notice-warn">
          <strong>
            {awaitingSignature.length} lease{awaitingSignature.length === 1 ? '' : 's'} waiting on a signature.
          </strong>{' '}
          {needsMySignature.length > 0
            ? `${needsMySignature.length} of them ${needsMySignature.length === 1 ? 'is' : 'are'} waiting on you. `
            : 'The tenants still have to sign. '}
          <Link to="/landlord/leases">Go and look</Link>.
        </div>
      ) : null}

      <div className="stat-row">
        <div className="stat-tile">
          <span className="stat-value">{live.length}</span>
          <span className="stat-label">Live listings</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{newApplications.length}</span>
          <span className="stat-label">New applications</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{formatNumber(totalViews)}</span>
          <span className="stat-label">Views across all listings</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{counts.messages}</span>
          <span className="stat-label">Unread messages</span>
        </div>
      </div>

      {monthlyIncome > 0 ? (
        <p className="dashboard-note">
          Houses currently marked as rented account for <strong>{formatXaf(monthlyIncome)}</strong> a month.
        </p>
      ) : null}

      {awaitingReview.length > 0 || rejected.length > 0 ? (
        <div className="notice notice-info">
          {awaitingReview.length > 0 ? (
            <span>
              {awaitingReview.length} listing{awaitingReview.length === 1 ? '' : 's'} waiting for an administrator to
              approve.{' '}
            </span>
          ) : null}
          {rejected.length > 0 ? (
            <span>
              {rejected.length} listing{rejected.length === 1 ? '' : 's'} were rejected and need editing.{' '}
              <Link to="/landlord/properties">Have a look</Link>.
            </span>
          ) : null}
        </div>
      ) : null}

      <section className="section-block">
        <div className="section-head">
          <h2>Applications needing attention</h2>
          <Link to="/landlord/applications" className="link-strong">
            Open the inbox
          </Link>
        </div>

        {applications.loading ? <Spinner /> : null}

        {!applications.loading && inbox.length === 0 ? (
          <EmptyState
            title="No applications yet"
            message="Listings with clear photos, an honest description and a video get applications much faster."
          />
        ) : (
          <ul className="inbox-strip">
            {inbox.slice(0, 5).map((application) => (
              <li key={application.id}>
                <div>
                  <StatusBadge status={application.status} />
                  <strong>{application.tenant?.name}</strong>
                  <span className="muted">
                    {application.property?.title} - applied {timeAgo(application.createdAt)}
                  </span>
                </div>
                <Link to={`/landlord/applications?propertyId=${application.propertyId}`} className="btn btn-outline btn-sm">
                  Review
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section-block">
        <div className="section-head">
          <h2>Your listings</h2>
          <Link to="/landlord/properties" className="link-strong">
            Manage all
          </Link>
        </div>

        {properties.loading ? <Spinner /> : null}

        {!properties.loading && listings.length === 0 ? (
          <EmptyState
            title="You have not listed anything yet"
            message="Add your first house. It takes about five minutes, and you can upload the photos afterwards."
            action={
              <Link to="/landlord/properties/new" className="btn btn-primary">
                Add a listing
              </Link>
            }
          />
        ) : (
          <ul className="mini-listing-list">
            {listings.slice(0, 5).map((property) => (
              <li key={property.id}>
                <Link to={`/properties/${property.id}`} className="mini-media">
                  {property.coverImage ? <img src={property.coverImage} alt="" /> : <span>No photo</span>}
                </Link>
                <div className="mini-body">
                  <strong>{property.title}</strong>
                  <span className="muted">
                    {property.city} - {formatXaf(property.price)} / month
                  </span>
                </div>
                <div className="mini-stats">
                  <span>{formatNumber(property.viewCount)} views</span>
                  <span>{property.applicationCount} applications</span>
                </div>
                <div className="mini-actions">
                  <StatusBadge status={property.moderationStatus === 'approved' ? property.status : property.moderationStatus} />
                  <Link to={`/landlord/properties/${property.id}/edit`} className="btn btn-ghost btn-sm">
                    Edit
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default LandlordDashboard;
